import styled from 'styled-components';
import { AchievementBadgeStack, AchievementBadgeStackProps, BadgeStackAchievement } from './AchievementBadgeStack';
import { AchievementDescription, DescribedAchievement } from './AchievementDescription';
import { ACHIEVEMENT_BADGE_SIZES_PX as SIZES_PX, AchievementBadgeSize } from './achievementBadgeTokens';
import { Tooltip } from '../tooltip/Tooltip';

export type ShowcaseAchievement = BadgeStackAchievement & DescribedAchievement & {
	/** Badges sharing a type collapse into one stack. */
	typeId: string;
}

type StackProps = Pick<AchievementBadgeStackProps, 'size' | 'variant' | 'baseUrl' | 'className'>;

export type AchievementShowcaseProps = StackProps & {
	achievements: ShowcaseAchievement[];
	/** A hover card per stack: name, description, and every date it was given. */
	showTooltip?: boolean;
	/** See `AchievementDescription`. */
	privacyNotice?: string;
}

const Container = styled.div<{ $size: AchievementBadgeSize }>`
	display: flex;
	align-items: center;
	gap: 6px;
	height: ${props => SIZES_PX[props.$size]}px;
`;

export function AchievementShowcase({
	achievements, size = 'small', variant = 'dark', baseUrl, showTooltip = false, privacyNotice, className,
}: AchievementShowcaseProps) {
	const stacks = new Map<string, ShowcaseAchievement[]>();
	achievements.forEach(achievement =>
		stacks.set(achievement.typeId, [...stacks.get(achievement.typeId) ?? [], achievement]));

	return <Container $size={size} className={className}>
		{Array.from(stacks, ([typeId, stack]) => {
			// A div, not the stack itself: Tooltip needs a DOM node to hang its ref on.
			const badges = <div key={typeId}>
				<AchievementBadgeStack achievements={stack} size={size} variant={variant} baseUrl={baseUrl} />
			</div>;
			return showTooltip
				? <Tooltip key={typeId} placement="top" delayShow={100}
					content={<AchievementDescription achievements={stack} privacyNotice={privacyNotice} />}>
					{badges}
				</Tooltip>
				: badges;
		})}
	</Container>;
}
